import { AnimatePresence, motion } from "framer-motion";

import { Button } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn, useIsOpen } from "@virtual-brain-lib/frontend-design-system/utils";
import type {
	MessageToolAnalysisData,
	MessageToolAnalysisDocument,
} from "@virtual-brain-lib/types";

import { AnalysisDocumentRow } from "./AnalysisDocumentRow";
import { ToolCardAllAnalysisDocumentsModal } from "./ToolCardAllAnalysisDocumentsModal";

const MAX_VISIBLE = 5;

interface ToolCardDocumentAnalysisListProps {
	readonly documents: MessageToolAnalysisData["documents"];
	readonly onDocumentClick?: (doc: MessageToolAnalysisDocument) => void;
	readonly className?: string;
}

export function ToolCardDocumentAnalysisList({
	documents,
	onDocumentClick,
	className,
}: ToolCardDocumentAnalysisListProps) {
	const modal = useIsOpen();

	if (documents.length === 0) {
		return null;
	}

	const visible = documents.length <= MAX_VISIBLE ? documents : documents.slice(0, MAX_VISIBLE);
	const hiddenCount = documents.length - visible.length;

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			<div className="flex flex-col">
				<AnimatePresence initial={false}>
					{visible.map((doc) => (
						<motion.div
							key={doc.id}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							initial={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							<AnalysisDocumentRow document={doc} onDocumentClick={onDocumentClick} />
						</motion.div>
					))}
				</AnimatePresence>
			</div>

			{hiddenCount > 0 && (
				<Button
					classNames={{ wrapper: "-ml-1.5 self-start" }}
					color="secondary"
					size="xs"
					variant="ghost"
					onClick={modal.open}
				>
					Show all
				</Button>
			)}

			<ToolCardAllAnalysisDocumentsModal
				close={modal.close}
				documents={documents}
				isOpen={modal.isOpen}
				onDocumentClick={onDocumentClick}
			/>
		</div>
	);
}
